import React, { useMemo, useState } from 'react';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { productOptions_productOptions_items_ConfigurableProduct } from 'src/queries/__generated__/productOptions';
import { Price } from 'src/components/Helpers/Price';
import { Svg } from 'src/components/Svg/Svg';
import { StockStatus } from 'src/types/global-types';
import { Button } from 'src/components/Button/Button';

import { optionsAndVariants } from '../../ProductDetail/utils/product-options';
import { optionsToColorSwatches, optionsToSizes, BaseVariant } from '../../ProductDetail/utils/product-detail.types';
import { upsell_upsell_items_ConfigurableProduct } from '../../../queries/__generated__/upsell';
import { BasketUpsellProductSwatch } from '../../Basket/upsell/BasketUpsellProductSwatch';
import { UpsellActionsLoader } from '../../Basket/upsell/UpsellActionsLoader';
import { useFormQuantity } from '../../Basket/upsell/useFormQuantity';

import { ProductSizesVertical } from './ProductSizesVertical';
import { BasketUpsellForm } from './BasketUpsellForm';
import classes from './BasketUpsellItem.scss';

const selectVariant = (
    variants: BaseVariant[],
    color: number,
    size: string | number,
    setSize: React.Dispatch<React.SetStateAction<number | string>>,
    quantity: number,
    setQuantity: React.Dispatch<React.SetStateAction<number>>,
): BaseVariant => {
    // Get all variants with a chosen color
    const findByColor = variants.filter((curr) => String(curr.attributes[0].value_index) === String(color) && curr);
    // Find variant with a chosen color and size (initial size or previously selected)
    const findBySize = findByColor.filter((curr) => String(curr.attributes[1].value_index) === String(size) && curr);

    const findByColorAndInStockFn = (findByColor: BaseVariant[], findByColorAndInStock?: BaseVariant) => {
        if (findByColorAndInStock) {
            setSize(findByColorAndInStock.attributes[1].value_index);
            if (quantity > findByColorAndInStock.max_qty!) {
                setQuantity(findByColorAndInStock.max_qty!);
            }
            return findByColorAndInStock;
        } else {
            // If there was no other size with stock status === InStock, return variant with no quantity and set form to disabled
            setSize(findByColor[0].attributes[1].value_index);
            setQuantity(0);
            return findByColor[0];
        }
    };

    if (findBySize.length === 0) {
        // If there is no variant with a chosen size and the chosen color, find first variant's size with stock_status === InStock
        const findByColorAndInStock = findByColor.find((variant) => variant.stock_status !== StockStatus.OutOfStock);
        return findByColorAndInStockFn(findByColor, findByColorAndInStock);
    } else {
        // If variant with a chosen color and size exist but is out of stock
        if (findBySize[0]?.stock_status === StockStatus.OutOfStock) {
            // If there is no variant with a chosen size and the chosen color, find first variant's size with stock_status === InStock
            const findByColorAndInStock = findByColor.find(
                (variant) => variant.stock_status !== StockStatus.OutOfStock,
            );
            return findByColorAndInStockFn(findByColor, findByColorAndInStock);
        } else {
            // If there is variant with a choosen color and size and status_stock === InStock return in
            setSize(findBySize[0].attributes[1].value_index);
            if (quantity > findBySize[0].max_qty!) {
                setQuantity(findBySize[0].max_qty!);
            }
            return findBySize[0];
        }
    }
};

type BasketUpsellItemProps = {
    item: upsell_upsell_items_ConfigurableProduct;
};

export const BasketUpsellItem: React.FC<BasketUpsellItemProps> = React.memo(({ item }) => {
    /**
     * Get options and variants
     */
    const { options, variants } = optionsAndVariants(
        {
            configurable_options: item.configurable_options,
            variants: item.variants,
        } as productOptions_productOptions_items_ConfigurableProduct,
        true,
    );

    /**
     * Get colors and sizes
     */
    const colors = optionsToColorSwatches(options);
    const sizes = optionsToSizes(options);
    const sizesMapped = sizes.map((size) => ({ id: size.id, value: size }));

    const { vat } = useVatCausesRender();

    /**
     * Set state
     */
    const [color, setColor] = useState<number>(colors[0].value_index);
    const [size, setSize] = useState<string | number>(sizes[0].id);
    const { quantity, onInputChange, setQuantity } = useFormQuantity(0);
    const [showMore, setShowMore] = useState(true);

    /**
     * Select proper Variant based on color and size
     */

    const variant: BaseVariant = useMemo(
        () => selectVariant(variants, color, size, setSize, quantity, setQuantity),
        [color, size, setSize, quantity, setQuantity],
    );

    /**
     * Get color index
     */
    const colorIndex = variant?.attributes.find((x) => x.code === 'color')?.value_index;

    /**
     * Grab all the sizes for the given color
     */
    const { sizesForColor } = useMemo(() => {
        const sizesForColorNotSorted: any = [];
        variants.map((variant) => {
            if (variant.color_id === colorIndex) {
                const match = sizesMapped.filter((el) => {
                    return el.id === variant.size_id;
                });
                if (match) {
                    // Grab all the sizes for the given color
                    match[0].value.maxQty = variant.max_qty;
                    sizesForColorNotSorted.push({
                        ...match[0].value,
                        simpleProductId: variant.id,
                        simpleProductSku: variant.sku,
                        stock: variant.stock_status,
                    });
                }
            }
        });

        /**
         * Keep the same order of sizes
         */
        const arrayOfSizesId = sizesMapped.map((el) => el.id);
        const sizesForColor = [...sizesForColorNotSorted].sort(
            (a, b) => arrayOfSizesId.indexOf(a.id) - arrayOfSizesId.indexOf(b.id),
        );
        return { sizesForColor };
    }, [colorIndex, variants, sizesMapped]);

    /**
     * Get product prices
     */
    const productPrice = {
        price_incl: [...item.margin_tier_prices].sort((a, b) => a!.price_incl - b!.price_incl)[0]?.price_incl as
            string | number,
        price: [...item.margin_tier_prices].sort((a, b) => a!.price - b!.price)[0]?.price as string | number,
    };

    const swatchTotal = colors.length;
    const maxUpsellSwatches = 5;

    return (
        <li className={classes.upsellItem}>
            <div className="BasketUpsellItem__content">
                <ProductInformation color={color} item={item} variant={variant} productPrice={productPrice} vat={vat} />
            </div>
            <div className="BasketUpsellItem__form">
                <BasketUpsellForm
                    variant={variant}
                    parentSku={item.sku}
                    sizes={sizes}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    setSize={setSize}
                    setColor={setColor}
                    color={colors[0].value_index}
                >
                    <div>
                        {colors?.length > 0 && (
                            <BasketUpsellProductSwatch
                                swatches={colors}
                                setColor={setColor}
                                color={color}
                                showMore={showMore}
                            />
                        )}
                    </div>
                    <div>
                        {swatchTotal > maxUpsellSwatches ? (
                            <Button
                                onClick={() => setShowMore(showMore ? false : true)}
                                className={classes.swatchesMore}
                                variant="light"
                            >
                                {showMore ? 'More colours' : 'Hide colours'}
                            </Button>
                        ) : (
                            <div className={classes.swatchesMoreHidden}></div>
                        )}
                        {sizes?.length && (
                            <ProductSizesVertical
                                size={size}
                                sizes={sizesForColor}
                                quantity={quantity}
                                onInputChange={onInputChange}
                                setSize={setSize}
                                setQuantity={setQuantity}
                                stockQty={variant.max_qty || 0}
                            />
                        )}
                        <UpsellActionsLoader quantity={quantity} />
                    </div>
                </BasketUpsellForm>
            </div>
        </li>
    );
});

type ProductInformationProps = {
    color: number | null;
    item: upsell_upsell_items_ConfigurableProduct;
    variant: BaseVariant;
    productPrice: {
        price: number | string;
        price_incl: number | string;
    };
    vat: boolean;
};

const ProductInformation: React.FC<ProductInformationProps> = ({ color, item, variant, productPrice, vat }) => {
    return (
        <>
            <div className={classes.upsellImageWrapper}>
                {item.can_customise && <Svg className={classes.upsellImageLogo} name="addIcon" />}
                <img
                    src={
                        !color || variant?.image_url === undefined
                            ? (item.small_image?.url as string)
                            : (variant?.image_url as string)
                    }
                    alt={(item.small_image?.label as string) || (item.name as string)}
                    className={classes.upsellImage}
                />
                <small className={classes.upsellImageBestseller}>
                    <Svg name="star" /> Bestseller
                </small>
            </div>
            <p className={classes.upsellItemTitle}>{item.name}</p>
            <span className={classes.upsellItemPrice}>
                From{' '}
                <strong>
                    <Price price={productPrice.price} priceIncl={productPrice.price_incl} vat={vat} />
                </strong>{' '}
                {vat ? 'inc' : 'ex'}. VAT
            </span>
        </>
    );
};
